import { useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { RangeKey } from "@k8s-dashboard/contracts";
import { useDashboardStore } from "./dashboardStore";

const RANGES: RangeKey[] = ["30d", "7d", "1d", "1h", "custom"];
export const REFRESH_OPTIONS = [
  { value: 0, label: "수동" },
  { value: 10_000, label: "10초" },
  { value: 30_000, label: "30초" },
  { value: 60_000, label: "1분" },
];

/**
 * 화면이 쓰는 Scope · 시간 범위 · 자동 갱신 주기.
 *
 * - 시간 범위·자동 갱신 주기는 **URL 쿼리**에 둡니다 — 링크 하나로 같은 시간 창을
 *   재현할 수 있어야 장애 대응 중 공유가 됩니다.
 * - 클러스터와 전역 namespace는 **앱 상태**입니다(#94). 클러스터는 단일 배포라 고르지 않고,
 *   namespace는 Header에서 고르며 브라우저에 보관됩니다. 둘 다 URL에 쓰지 않습니다.
 *   원천과 규칙은 `dashboardStore.ts`에 있습니다.
 */
export function useDashboardParams() {
  const [params, setParams] = useSearchParams();
  const clusterId = useDashboardStore((s) => s.clusterId);
  const namespace = useDashboardStore((s) => s.namespace);
  const setNamespace = useDashboardStore((s) => s.setNamespace);

  const rangeParam = params.get("range") as RangeKey | null;
  /* custom 범위의 시작·종료(epoch ms 문자열). 서버는 from/to가 있으면 그것을 Custom으로 읽고
     range 키는 무시합니다(timerange.Parse). */
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  let range: RangeKey = rangeParam && RANGES.includes(rangeParam) ? rangeParam : "1h";
  /* range=custom은 from/to가 있을 때만 유효합니다. 없으면(직접 만든/오래된 링크) 기본
     프리셋으로 떨어뜨려 서버에 400을 유발하지 않습니다. (#58) */
  if (range === "custom" && !(from && to)) range = "1h";
  const refreshMs = Number(params.get("refresh") ?? 30_000);
  const safeRefreshMs = Number.isFinite(refreshMs) ? refreshMs : 30_000;
  const pathParams = useParams();

  /* URL이 원천인 값(range·refresh·path parameter)만 스토어에 미러링합니다. */
  const pathKey = JSON.stringify(pathParams);
  useEffect(() => {
    useDashboardStore.getState().syncFromUrl({ range, refreshMs: safeRefreshMs, pathParams });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathParams는 pathKey로 비교합니다.
  }, [range, safeRefreshMs, pathKey]);

  const patch = useCallback(
    (next: Partial<{ ns: string; range: RangeKey; from: string; to: string; refresh: number }>) => {
      /* 전역 namespace는 URL이 아니라 스토어로 갑니다. (#94) */
      if (next.ns !== undefined) setNamespace(next.ns);
      if (next.range === undefined && next.from === undefined && next.to === undefined && next.refresh === undefined) {
        return;
      }
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          /* 기본값과 같은 파라미터는 URL에 남기지 않습니다 — 링크 재현성은 그대로면서
             URL이 짧아집니다. range=1h·refresh=30초가 기본. (#31 후속) */
          if (next.range !== undefined) {
            if (next.range === "1h") p.delete("range");
            else p.set("range", next.range);
            /* 프리셋으로 바꾸면 custom의 from/to를 지웁니다 — 화면에 남은 custom 창이
               새 프리셋 조회에 섞이지 않게 합니다. custom 적용은 아래에서 다시 채웁니다. */
            if (next.range !== "custom") {
              p.delete("from");
              p.delete("to");
            }
          }
          if (next.from !== undefined) {
            if (next.from === "") p.delete("from");
            else p.set("from", next.from);
          }
          if (next.to !== undefined) {
            if (next.to === "") p.delete("to");
            else p.set("to", next.to);
          }
          if (next.refresh !== undefined) {
            if (next.refresh === 30_000) p.delete("refresh");
            else p.set("refresh", String(next.refresh));
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams, setNamespace],
  );

  return useMemo(
    () => ({
      clusterId,
      namespace,
      range,
      from,
      to,
      refreshMs: safeRefreshMs,
      patch,
    }),
    [clusterId, namespace, range, from, to, safeRefreshMs, patch],
  );
}
